// Web-Push (PWA-Benachrichtigungen) — Client-Helfer. Subscribe/Unsubscribe gegen das
// Backend (push_routes). Der globale fetch-Wrapper (index.tsx) hängt den Bearer an /api/*.
import { API_BASE_URL } from '../config';

function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(b64);
  return Uint8Array.from(Array.from(raw, (c) => c.charCodeAt(0)));
}

export function pushSupported(): boolean {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

export type PushPermission = 'unsupported' | 'default' | 'granted' | 'denied';

export function pushPermission(): PushPermission {
  if (!pushSupported()) return 'unsupported';
  return Notification.permission as PushPermission;
}

export async function isPushEnabled(): Promise<boolean> {
  if (!pushSupported() || Notification.permission !== 'granted') return false;
  try {
    const reg = await navigator.serviceWorker.ready;
    return !!(await reg.pushManager.getSubscription());
  } catch {
    return false;
  }
}

// Permission anfragen, Push-Subscription erstellen und ans Backend melden.
// Gibt true zurück, wenn aktiviert; false bei abgelehnter Permission.
export async function enablePush(): Promise<boolean> {
  if (!pushSupported()) throw new Error('Push wird auf diesem Gerät/Browser nicht unterstützt.');
  const perm = await Notification.requestPermission();
  if (perm !== 'granted') return false;
  const reg = await navigator.serviceWorker.ready;
  const { public_key } = await fetch(`${API_BASE_URL}/api/push/vapid-key`).then((r) => r.json());
  if (!public_key) throw new Error('Server liefert keinen VAPID-Key.');
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(public_key),
    });
  }
  const j = sub.toJSON();
  await fetch(`${API_BASE_URL}/api/push/subscribe`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ endpoint: j.endpoint, keys: j.keys }),
  });
  return true;
}

export async function disablePush(): Promise<void> {
  const reg = await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.getSubscription();
  if (sub) {
    const j = sub.toJSON();
    await fetch(`${API_BASE_URL}/api/push/unsubscribe`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ endpoint: j.endpoint, keys: j.keys }),
    }).catch(() => {});
    await sub.unsubscribe();
  }
}

export async function sendTestPush(): Promise<number> {
  const d = await fetch(`${API_BASE_URL}/api/push/test`, { method: 'POST' }).then((r) => r.json());
  return d.sent || 0;
}
